import React, { useCallback, useState, useEffect } from 'react';
import { ReactFlow, addEdge, applyEdgeChanges, applyNodeChanges, MiniMap } from '@xyflow/react';
import { EmailSentNode } from '../components/automationNodes/EmailSentNode';
import { WaitNode } from '../components/automationNodes/WaitNode';
import { CheckEmailNode } from '../components/automationNodes/CheckEmailNode';
import { FollowUpEmailNode } from '../components/automationNodes/FollowUpEmailNode';
import { useAuth } from '../context/AuthContext';
import '@xyflow/react/dist/style.css';
import {
    Drawer,
    Box,
    Button,
    Snackbar,
    Alert,
} from '@mui/material';
import EmailEditor from '../components/EmailEditor';
import { useParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { initialNodes, initialEdges } from '../constants/flowConstants';
import { db } from '../firebase';

const rfStyle = {
    backgroundColor: '#0D47A1',
};


const nodeTypes = {
    emailSent: EmailSentNode,
    wait: WaitNode,
    checkEmail: CheckEmailNode,
    followUp: FollowUpEmailNode,
};

const EmailAutomation = () => {
    const { user } = useAuth();
    const { id } = useParams();
    const [nodes, setNodes] = useState(initialNodes);
    const [edges, setEdges] = useState(initialEdges);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [followUpEmail, setFollowUpEmail] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

    const onNodesChange = useCallback((changes) => {
        setNodes((nds) => applyNodeChanges(changes, nds));
    }, []);

    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );

    const onConnect = useCallback(
        (connection) => setEdges((eds) => addEdge(connection, eds)),
        []
    );

    const onNodeClick = useCallback((event, node) => {
        if (node.type === 'followUp') {
            setIsDrawerOpen(true);
        }
    }, []);

    const showAlert = (message, severity = 'success') => {
        setAlert({ show: true, message, severity });
    };

    const saveAutomation = async () => {
        if (!user?.email || !id) {
            showAlert('Cannot save automation', 'error');
            return;
        }

        setLoading(true);
        try {
            // Create a deep copy of nodes without the onChange function
            const sanitizedNodes = nodes.map(node => {
                if (node.type === 'wait') {
                    const { onChange, ...sanitizedData } = node.data;
                    return {
                        ...node,
                        data: sanitizedData
                    };
                }
                return node;
            });

            const waitNode = sanitizedNodes.find(n => n.type === 'wait');

            const docRef = doc(db, 'emailAutomations', id);
            await updateDoc(docRef, {
                nodes: sanitizedNodes,
                edges: edges,
                followUpEmail: followUpEmail,
                waitDays: waitNode?.data?.days || 3
            });

            showAlert('Automation updated successfully');
        } catch (error) {
            console.error('Error updating automation:', error);
            showAlert(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };



    const loadAutomation = async () => {
        if (!user?.email) return;

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACK_END_URL}/applybridge/get-automation/${id}`);
            if (!response.ok) {
                throw new Error('Failed to load automation');
            }

            const data = await response.json();
            if (data.nodes) {
                setNodes(data.nodes);
                setEdges(data.edges);
                setFollowUpEmail(data.followUpEmail);
                setRecipientEmail(data.recipientEmail);
            }
        } catch (error) {
            console.error('Error loading automation:', error);
            showAlert('Failed to load automation', 'error');
        } finally {
            setLoading(false);
        }
    };

    const updateFollowUpEmail = async (newEmail) => {
        if (!user?.email || !id) { // id from useParams
            showAlert('Please login to update follow-up email', 'error');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACK_END_URL}/applybridge/update-followup-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    automationId: id, // Use the automation ID instead of email
                    followUpEmail: newEmail
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update follow-up email');
            }

            setFollowUpEmail(newEmail);
            showAlert('Follow-up email updated successfully');
        } catch (error) {
            console.error('Error updating follow-up email:', error);
            showAlert(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.email) {
            loadAutomation().then(() => {
                setNodes(prevNodes =>
                    prevNodes.map(node => {
                        if (node.type === 'wait') {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    days: node.data?.days || 3,
                                    onChange: (days) => {
                                        console.log('Wait days changed to:', days); // Add this for debugging
                                        setNodes(nds =>
                                            nds.map(n =>
                                                n.id === node.id
                                                    ? { ...n, data: { ...n.data, days } }
                                                    : n
                                            )
                                        );
                                    }
                                }
                            };
                        }
                        return node;
                    })
                );
            });
        }
    }, [user]);

    return (
        <>
            <div style={{ width: '100%', height: '100vh' }}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes}
                    fitView
                    style={rfStyle}
                >
                    <MiniMap nodeStrokeWidth={3} />
                </ReactFlow>

                <Button
                    variant="contained"
                    onClick={saveAutomation}
                    disabled={loading}
                    style={{
                        position: 'absolute',
                        bottom: 20,
                        right: 20,
                        zIndex: 4,
                    }}
                >
                    {loading ? 'Saving...' : 'Save Automation'}
                </Button>
            </div>

            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                sx={{ '& .MuiDrawer-paper': { width: '40%' } }}
            >
                <Box sx={{ p: 3 }}>
                    <EmailEditor
                        content={followUpEmail}
                        recipientEmail={recipientEmail}
                        subject="Follow-up: Job Application"
                        onChange={(newEmail) => {
                            setFollowUpEmail(newEmail);
                            updateFollowUpEmail(newEmail);
                        }}
                    />
                </Box>
            </Drawer>

            <Snackbar
                open={alert.show}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, show: false })}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, show: false })}
                    severity={alert.severity}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EmailAutomation;